import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Styled from '../LoyaltyPageSeparated.styles';

import { selectors } from '../../LoyaltyPage/reducer';
import { CurrentReceiptItem } from '../../LoyaltyPage/types';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';

interface ReceiptTableSeparatedProps {
  currentReceipt: CurrentReceiptItem[];
  showWithBonuses: boolean;
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  showCurrency?: boolean;
  showBonusAccrual?: boolean;
  compact?: boolean;
}

const ReceiptTableSeparated: React.FC<ReceiptTableSeparatedProps> = ({
  currentReceipt,
  showWithBonuses,
  partnerPercent,
  isStadaBonusesCredit,
  showCurrency = true,
  showBonusAccrual = true,
  compact = false,
}) => {
  const { t } = useTranslation();
  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);
  const clientInfoBalance = (clientInfo && clientInfo.balance) ?? 0;
  const stadaBalance = (stadaInfo && stadaInfo.balance) ?? 0;
  const currencySuffix = showCurrency ? ' ₸' : '';

  // Есть ли в текущем чеке штада-товары
  const hasStadaItems = useMemo(
    () => currentReceipt?.some((item) => (item.stadaPercent ?? 0) > 0),
    [currentReceipt]
  );

  const rows = useMemo(
    () =>
      distributeClientBonus(
        currentReceipt,
        showWithBonuses ? clientInfoBalance : 0,
        partnerPercent,
        showWithBonuses && hasStadaItems && isStadaBonusesCredit
          ? stadaBalance
          : 0
      ),
    [
      currentReceipt,
      clientInfoBalance,
      stadaBalance,
      showWithBonuses,
      hasStadaItems,
      isStadaBonusesCredit,
      partnerPercent,
    ]
  );

  const showStadaColumn =
    !compact &&
    showBonusAccrual &&
    rows.some((row) => row.stadaPercent > 0) &&
    isStadaBonusesCredit;
  const columnCount = compact
    ? 5
    : 6 + Number(showBonusAccrual) + Number(showStadaColumn);

  return (
    <Styled.TableWrapper $compact={compact}>
      <Styled.StyledTable $compact={compact}>
        <Styled.THead>
          <tr>
            {!compact && <Styled.Th>№</Styled.Th>}
            <Styled.Th>{t('LoyaltyPage.Table.PRODUCT_NAME')}</Styled.Th>
            <Styled.Th>{t('LoyaltyPage.Table.PRODUCT_COUNT')}</Styled.Th>
            <Styled.Th>{t('LoyaltyPage.Table.PRODUCT_PRICE')}</Styled.Th>
            <Styled.Th>{t('LoyaltySeparated.TABLE_DISCOUNT')}</Styled.Th>
            <Styled.Th>{t('LoyaltyPage.Table.SUM')}</Styled.Th>
            {!compact && showBonusAccrual && (
              <Styled.Th>{t('LoyaltySeparated.TABLE_BONUS')}</Styled.Th>
            )}
            {showStadaColumn && (
              <Styled.Th>{t('LoyaltySeparated.TABLE_BONUS_STADA')}</Styled.Th>
            )}
          </tr>
        </Styled.THead>
        <Styled.TBody>
          {rows.length === 0 ? (
            <Styled.EmptyRow>
              <td colSpan={columnCount}>{t('LoyaltySeparated.TABLE_EMPTY')}</td>
            </Styled.EmptyRow>
          ) : (
            rows.map((row, index) => (
              <tr key={row.sku}>
                {!compact && <Styled.Td>{index + 1}</Styled.Td>}
                <Styled.Td>{row.name}</Styled.Td>
                <Styled.Td>{row.part_quantity}</Styled.Td>
                <Styled.Td>
                  {row.unit_price.toFixed(2)}
                  {currencySuffix}
                </Styled.Td>
                <Styled.Td>
                  {row.appliedBonus > 0 ? (
                    <Styled.DiscountBadge>
                      −{row.appliedBonus}
                      {currencySuffix}
                    </Styled.DiscountBadge>
                  ) : (
                    <span style={{ color: '#d1d5db' }}>—</span>
                  )}
                </Styled.Td>
                <Styled.Td>
                  {row.appliedBonus > 0 ? (
                    <>
                      <Styled.OldPrice>{row.total_price}</Styled.OldPrice>
                      <Styled.NewPrice>
                        {Math.round(row.total_price - row.appliedBonus)}
                        {currencySuffix}
                      </Styled.NewPrice>
                    </>
                  ) : (
                    <span>
                      {row.total_price}
                      {currencySuffix}
                    </span>
                  )}
                </Styled.Td>
                {!compact && showBonusAccrual && (
                  <Styled.Td>
                    {row.bonusesToClient > 0 ? (
                      <Styled.BonusBadge>
                        +{row.bonusesToClient} Б
                      </Styled.BonusBadge>
                    ) : (
                      <span style={{ color: '#d1d5db' }}>0 Б</span>
                    )}
                  </Styled.Td>
                )}
                {showStadaColumn && (
                  <Styled.Td>
                    {row.stadaBonus > 0 ? (
                      <Styled.BonusBadge>+{row.stadaBonus} Б</Styled.BonusBadge>
                    ) : (
                      <span style={{ color: '#d1d5db' }}>0 Б</span>
                    )}
                  </Styled.Td>
                )}
              </tr>
            ))
          )}
        </Styled.TBody>
      </Styled.StyledTable>
    </Styled.TableWrapper>
  );
};

export default ReceiptTableSeparated;
